import React, { useState, useEffect } from 'react';
import {
    X, Save, Eye, Code, Monitor, Smartphone, Tablet,
    Maximize2, Minimize2, Sparkles, AlertCircle, RefreshCcw, Send, Check
} from 'lucide-react';
import { emailApi } from '../../lib/api/email';

interface EmailEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialContent: string;
    subject?: string;
    onSave: (htmlContent: string) => void;
}

type DevicePreview = 'desktop' | 'tablet' | 'mobile';

export const EmailEditorModal: React.FC<EmailEditorModalProps> = ({
    isOpen,
    onClose,
    initialContent,
    subject = 'Dashbal Test Email',
    onSave
}) => {
    const [codeContent, setCodeContent] = useState(initialContent);
    const [devicePreview, setDevicePreview] = useState<DevicePreview>('desktop');
    const [showPreview, setShowPreview] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Test send state
    const [showTestSend, setShowTestSend] = useState(false);
    const [testRecipient, setTestRecipient] = useState('');
    const [sendingTest, setSendingTest] = useState(false);
    const [testSent, setTestSent] = useState(false);
    const [testError, setTestError] = useState<string | null>(null);

    // Sync content when modal opens
    useEffect(() => {
        if (isOpen) {
            setCodeContent(initialContent);
        }
    }, [initialContent, isOpen]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(codeContent);
            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    const handleSendTest = async () => {
        if (!testRecipient || !codeContent) return;
        setSendingTest(true);
        setTestError(null);
        try {
            await emailApi.sendTestEmail({
                to: testRecipient,
                subject: subject,
                html: codeContent
            });
            setTestSent(true);
            setTimeout(() => {
                setTestSent(false);
                setShowTestSend(false);
            }, 3000);
        } catch (error: any) {
            console.error('Failed to send test email:', error);
            setTestError(error.message || 'Failed to send test email');
        }
        setSendingTest(false);
    };

    const getPreviewWidth = () => {
        switch (devicePreview) {
            case 'mobile': return '375px';
            case 'tablet': return '768px';
            default: return '100%';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 font-sans">
            <div className={`bg-white flex flex-col shadow-2xl transition-all duration-500 ease-in-out overflow-hidden ${isFullscreen ? 'w-full h-full rounded-none' : 'w-[95%] h-[90%] rounded-2xl'
                }`}>

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-white">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-accent-primary flex items-center justify-center shadow-lg shadow-accent-primary/20 text-white">
                                <Code size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-display font-bold text-text-primary">Email Code Editor</h2>
                                <p className="text-xs text-neutral-500 font-medium">Full HTML & CSS Control</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Device Toggles */}
                        <div className="flex items-center bg-neutral-100 rounded-xl p-1 mr-2">
                            <button
                                onClick={() => setDevicePreview('desktop')}
                                className={`p-1.5 rounded-md transition-all ${devicePreview === 'desktop' ? 'bg-white shadow-sm text-accent-primary' : 'text-neutral-500'}`}
                                title="Desktop View"
                            >
                                <Monitor size={16} />
                            </button>
                            <button
                                onClick={() => setDevicePreview('tablet')}
                                className={`p-1.5 rounded-md transition-all ${devicePreview === 'tablet' ? 'bg-white shadow-sm text-accent-primary' : 'text-neutral-500'}`}
                                title="Tablet View"
                            >
                                <Tablet size={16} />
                            </button>
                            <button
                                onClick={() => setDevicePreview('mobile')}
                                className={`p-1.5 rounded-md transition-all ${devicePreview === 'mobile' ? 'bg-white shadow-sm text-accent-primary' : 'text-neutral-500'}`}
                                title="Mobile View"
                            >
                                <Smartphone size={16} />
                            </button>
                        </div>

                        <div className="h-6 w-px bg-neutral-200 mx-1" />

                        <button
                            onClick={() => setShowPreview(!showPreview)}
                            className={`px-4 py-2.5 text-xs font-bold rounded-xl flex items-center gap-2 transition-all shadow-sm ${showPreview
                                ? 'bg-neutral-900 text-white'
                                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 border border-neutral-200'
                                }`}
                        >
                            <Eye size={14} />
                            {showPreview ? 'Hide Preview' : 'Show Preview'}
                        </button>

                        <div className="relative">
                            <button
                                onClick={() => setShowTestSend(!showTestSend)}
                                className={`px-4 py-2.5 text-xs font-bold rounded-xl transition-all shadow-sm flex items-center gap-2 ${showTestSend
                                    ? 'bg-[#c7613d] text-white'
                                    : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                                    }`}
                            >
                                <Send size={14} />
                                Send Test
                            </button>

                            {showTestSend && (
                                <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-neutral-200 p-4 z-50 animate-in fade-in slide-in-from-top-2">
                                    <h4 className="text-xs font-bold text-neutral-900 uppercase tracking-wide mb-3">Send Test Email</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <input
                                                type="email"
                                                placeholder="Enter recipient email..."
                                                value={testRecipient}
                                                onChange={(e) => setTestRecipient(e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:ring-2 focus:ring-[#c7613d]/20 focus:border-[#c7613d] outline-none transition-all text-neutral-900"
                                                autoFocus
                                            />
                                        </div>
                                        {testError && (
                                            <div className="flex items-center gap-2 text-red-600 text-[10px] bg-red-50 p-2 rounded-lg">
                                                <AlertCircle size={12} />
                                                <span>{testError}</span>
                                            </div>
                                        )}
                                        <button
                                            onClick={handleSendTest}
                                            disabled={sendingTest || !testRecipient || !codeContent}
                                            className={`w-full py-2 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${testSent
                                                ? 'bg-green-500 text-white'
                                                : 'bg-[#c7613d] text-white hover:bg-[#b05536] disabled:opacity-50 disabled:cursor-not-allowed'
                                                }`}
                                        >
                                            {sendingTest ? (
                                                <RefreshCcw size={14} className="animate-spin" />
                                            ) : testSent ? (
                                                <>
                                                    <Check size={14} />
                                                    Sent!
                                                </>
                                            ) : (
                                                <>
                                                    <Send size={14} />
                                                    Send Now
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            className="p-2.5 text-neutral-500 hover:bg-neutral-100 border border-transparent hover:border-neutral-200 rounded-xl transition-colors"
                            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                        </button>

                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="ml-2 px-6 py-2.5 bg-accent-primary text-white rounded-xl hover:bg-accent-primary/90 flex items-center gap-2 font-bold shadow-lg shadow-accent-primary/20 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                        >
                            {isSaving ? <RefreshCcw size={16} className="animate-spin" /> : <Save size={16} />}
                            Save Changes
                        </button>

                        <button
                            onClick={onClose}
                            className="p-2.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-xl transition-colors ml-1"
                        >
                            <X size={22} />
                        </button>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 flex overflow-hidden bg-[#1e1e1e]">

                    {/* Code Editor */}
                    <div className={`flex flex-col transition-all duration-300 ${showPreview ? 'w-1/2 border-r border-[#333]' : 'w-full'}`}>
                        <div className="bg-[#2d2d2d] px-4 py-2 border-b border-[#333] flex items-center justify-between">
                            <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">HTML Source</span>
                            <div className="flex items-center gap-2">
                                <span className="flex h-2 w-2 rounded-full bg-success-500 animate-pulse"></span>
                                <span className="text-[10px] font-bold text-neutral-500 uppercase">Live Editor</span>
                            </div>
                        </div>
                        <textarea
                            value={codeContent}
                            onChange={(e) => setCodeContent(e.target.value)}
                            className="flex-1 p-6 font-mono text-sm leading-relaxed bg-[#1e1e1e] text-neutral-300 resize-none outline-none selection:bg-accent-primary/30"
                            spellCheck={false}
                            placeholder="Enter your email HTML here..."
                        />
                    </div>

                    {/* Live Preview */}
                    {showPreview && (
                        <div className="flex-1 bg-background-elevated flex flex-col relative overflow-hidden">
                            <div className="bg-white px-4 py-2 border-b border-neutral-200 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest flex items-center gap-1.5">
                                        <Monitor size={10} />
                                        Preview
                                    </span>
                                    <span className="px-1.5 py-0.5 rounded bg-neutral-100 text-[10px] text-neutral-400 font-bold uppercase tracking-tighter">
                                        {devicePreview}
                                    </span>
                                </div>
                            </div>

                            <div className="flex-1 overflow-auto p-8 flex justify-center items-start">
                                <div
                                    className="bg-white shadow-2xl shadow-neutral-900/10 transition-all duration-300 ease-in-out border border-neutral-200"
                                    style={{
                                        width: getPreviewWidth(),
                                        height: 'calc(100% - 20px)',
                                        maxWidth: '100%',
                                        borderRadius: devicePreview === 'desktop' ? '0' : '20px',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <iframe
                                        srcDoc={codeContent
                                            .replace(/{{logoUrl}}/g, 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAeCAYAAAA7MK6iAAAACXBIWXMAAAsTAAALEwEAmpwYAAAB/UlEQVR4nOWXwU7bQBCGLcoZ8QTlBUp7qDqzM96VXyFU8qmq2M0zQLjVV8QlpS9BL9wgPSZSxZoDgreouNKcUapVMN14HbIJPsFKv+TY+8+XnR2P1knyqkea4mciGBDhVZOYYcQMvSzL1ivPsMjWrZG9UquRNeqqUVoNrEl35kKZcRInOK58pVE/SqMmMbJNcGb4FQsmgnGe528mRbJWajmOB8vzAEyEN/8D4xER5L6YUfvwLPuwOdzNNmeCd5W2Wua+Si2PvDk3C8DQqT93oEVg97vuu9iVnZcDvvwCGzOp1rJvjTqclTz1nl+3Anb3rZa38cWlTloDl0Yaq9V9BPTuopu9aw38AEerZRGmeapLrfZ+f03fBtDngp81mOHaA58yw+GssO+DAWCj8qYpCGYoQs+jemn6cd6K4Wd858I/nq/LjPcR3e6vEGI7ALubzHgXAXYQ7WXqdokef9K4apcOZth/ImXfhBBQzXfprgXv1z3TbcMKHL7Hq4yYgnOF6m1R2Lmmk2iLCA+eWHHhCqlVMCK+dwUQs8dEaFoD0wpV3QqYV3iPW1oxLt25XhIYo44+dbCbU/e5WIuKaxBfXDBOkmTNyV3Hdy48C8BCfNpZovV9r3zuqLvEH+4E4ApOBOfzDvREMCSCPf9A764f2uxo/ocAnrnYjdBXM/4B1Z1SoeLTt4UAAAAASUVORK5CYII=')
                                            .replace(/{{logoBase64}}/g, 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAeCAYAAAA7MK6iAAAACXBIWXMAAAsTAAALEwEAmpwYAAAB/UlEQVR4nOWXwU7bQBCGLcoZ8QTlBUp7qDqzM96VXyFU8qmq2M0zQLjVV8QlpS9BL9wgPSZSxZoDgreouNKcUapVMN14HbIJPsFKv+TY+8+XnR2P1knyqkea4mciGBDhVZOYYcQMvSzL1ivPsMjWrZG9UquRNeqqUVoNrEl35kKZcRInOK58pVE/SqMmMbJNcGb4FQsmgnGe528mRbJWajmOB8vzAEyEN/8D4xER5L6YUfvwLPuwOdzNNmeCd5W2Wua+Si2PvDk3C8DQqT93oEVg97vuu9iVnZcDvvwCGzOp1rJvjTqclTz1nl+3Anb3rZa38cWlTloDl0Yaq9V9BPTuopu9aw38AEerZRGmeapLrfZ+f03fBtDngp81mOHaA58yw+GssO+DAWCj8qYpCGYoQs+jemn6cd6K4Wd858I/nq/LjPcR3e6vEGI7ALubzHgXAXYQ7WXqdokef9K4apcOZth/ImXfhBBQzXfprgXv1z3TbcMKHL7Hq4yYgnOF6m1R2Lmmk2iLCA+eWHHhCqlVMCK+dwUQs8dEaFoD0wpV3QqYV3iPW1oxLt25XhIYo44+dbCbU/e5WIuKaxBfXDBOkmTNyV3Hdy48C8BCfNpZovV9r3zuqLvEH+4E4ApOBOfzDvREMCSCPf9A764f2uxo/ocAnrnYjdBXM/4B1Z1SoeLTt4UAAAAASUVORK5CYII=')
                                            .replace(/{{frontendUrl}}/g, '#')
                                            .replace(/{{verificationUrl}}/g, '#')
                                            .replace(/{{resetUrl}}/g, '#')
                                            .replace(/{{unsubscribeUrl}}/g, '#')
                                            .replace(/{{userName}}/g, 'John Doe')
                                        }
                                        style={{ width: '100%', height: '100%', border: 'none' }}
                                        title="Email Preview"
                                        sandbox="allow-same-origin"
                                    />
                                </div>
                            </div>

                            {/* Info Banner */}
                            {(!codeContent.includes('<html') && codeContent.trim().length > 0) && (
                                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[85%] max-w-sm bg-warning-50 border border-warning-500/20 rounded-xl p-4 flex items-start gap-3 shadow-xl shadow-warning-500/10 backdrop-blur-sm">
                                    <AlertCircle className="text-warning-600 flex-shrink-0" size={18} />
                                    <div>
                                        <p className="text-[11px] font-bold text-warning-600 mb-0.5 uppercase tracking-wide">Snippet Mode</p>
                                        <p className="text-[11px] text-warning-600/80 leading-relaxed font-medium">
                                            Missing full HTML tags. This content will be wrapped in the Dashbal template shell.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-2 bg-white border-t border-neutral-200 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-neutral-400">
                            <Sparkles size={12} className="text-accent-primary" />
                            <span className="uppercase tracking-widest font-display">Dashbal Engine</span>
                        </div>
                    </div>
                    <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest font-display">
                        {codeContent.length.toLocaleString()} Bytes / Valid UTF-8
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailEditorModal;
